import { Link, useParams } from 'react-router-dom';
import { ArrowRight, FileText, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSections, useSectionChapters } from '../hooks/useSections';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { api } from '../lib/api';
import Breadcrumb from '../components/Breadcrumb';
import PermissionsPanel from '../components/PermissionsPanel';

export default function SectionPage() {
  const { sectionSlug } = useParams();
  const { data: secData, isLoading: secLoading } = useSections();
  const section = secData?.sections.find((s) => s.slug === sectionSlug);
  const { data: chData, isLoading: chLoading } = useSectionChapters(section?.id);
  const { user } = useAuth();
  const { lang } = useLanguage();

  if (secLoading || chLoading) return <div className="text-sm text-gray-500">Načítám…</div>;
  if (!section) return <div>Sekce nenalezena.</div>;

  const topLevel = (chData?.chapters ?? []).filter((c) => !c.parentChapterId);
  const sectionTitle = (lang === 'en' && section.titleEn) || section.titleCs;
  const sectionDescription = (lang === 'en' && section.descriptionEn) || section.descriptionCs;

  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  return (
    <div>
      <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: sectionTitle }]} />

      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold text-dark">{sectionTitle}</h1>
          {sectionDescription && <p className="mt-2 text-sm text-gray-600">{sectionDescription}</p>}
        </div>
        {canEdit && <NewChapterButton sectionId={section.id} />}
      </div>

      {topLevel.length === 0 ? (
        <div className="card p-12 text-center text-gray-500">Žádné kapitoly. Založ první.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {topLevel.map((ch) => (
            <Link
              key={ch.id}
              to={`/c/${section.slug}/${ch.slug}`}
              className="card flex items-center gap-4 p-5 transition-all hover:border-teal hover:shadow-lift"
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-teal-light text-teal">
                <FileText className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-dark">
                    {lang === 'en'
                      ? ch.publishedTitleEn || ch.draftTitleEn || ch.draftTitleCs
                      : ch.draftTitleCs}
                  </span>
                  {ch.requiresAcknowledgment === 1 && (
                    <span
                      title="Vyžaduje potvrzení přečtení"
                      className="inline-flex items-center gap-0.5 rounded-full bg-gold-soft px-1.5 py-0.5 text-[10px] font-semibold text-dark"
                    >
                      <ShieldAlert className="h-2.5 w-2.5 text-gold" />
                      Ack
                    </span>
                  )}
                </div>
                {!ch.publishedTitleCs && (
                  <div className="mt-0.5 text-[11px] uppercase tracking-wide text-warning">Nepublikováno</div>
                )}
              </div>
              <ArrowRight className="h-4 w-4 text-teal" />
            </Link>
          ))}
        </div>
      )}

      {user?.role === 'admin' && <PermissionsPanel scopeType="section" scopeId={section.id} />}
    </div>
  );
}

function NewChapterButton({ sectionId }: { sectionId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');

  const m = useMutation({
    mutationFn: () => api.post('/chapters', { section_id: sectionId, title_cs: title }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sections', sectionId, 'chapters'] });
      qc.invalidateQueries({ queryKey: ['sections', sectionId, 'chapters-sidebar'] });
      setOpen(false);
      setTitle('');
    },
  });

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-primary">
        <Plus className="h-4 w-4" /> Nová kapitola
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
      className="card flex gap-3 p-4"
    >
      <input
        className="input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Název kapitoly"
        autoFocus
      />
      <button type="submit" className="btn-primary" disabled={!title || m.isPending}>
        Vytvořit
      </button>
      <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
        Zrušit
      </button>
    </form>
  );
}
